import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import FilterCriteriaRow from "@via/pages/runs/components/filter-criteria-row";
import {
  type FilterCriteria,
  type FilterFieldOption,
} from "@via/hooks/runs-filter-types.ts";

interface RunsAdvancedFilterProps {
  isOpen: boolean;
  onToggle: () => void;
  criteria: FilterCriteria[];
  fieldOptions: FilterFieldOption[];
  onAddCriteria: () => void;
  onRemoveCriteria: (id: string) => void;
  onUpdateCriteria: (
    id: string,
    update: Partial<Omit<FilterCriteria, "id">>
  ) => void;
  onApply: () => void;
  onClear: () => void;
  hasActiveCriteria: boolean;
}

export default function RunsAdvancedFilter({
  isOpen,
  onToggle,
  criteria,
  fieldOptions,
  onAddCriteria,
  onRemoveCriteria,
  onUpdateCriteria,
  onApply,
  onClear,
  hasActiveCriteria,
}: RunsAdvancedFilterProps) {
  return (
    <div className="w-full mt-2">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
        <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
        <span className="flex-1 text-left">Advanced Filters</span>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="mt-3 rounded-md border border-gray-200 p-4">
          <div className="space-y-3">
            {criteria.map(c => (
              <FilterCriteriaRow
                key={c.id}
                criteria={c}
                fieldOptions={fieldOptions}
                onFieldChange={field => onUpdateCriteria(c.id, { field })}
                onValueChange={value => onUpdateCriteria(c.id, { value })}
                onRemove={() => onRemoveCriteria(c.id)}
              />
            ))}

            <button
              type="button"
              onClick={onAddCriteria}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
              <PlusIcon className="h-4 w-4" />
              Add criteria
            </button>
          </div>

          <div className="mt-4 border-t border-gray-200 pt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={onClear}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowPathIcon className="h-4 w-4" />
              Clear all
            </button>

            <button
              type="button"
              onClick={onApply}
              disabled={!hasActiveCriteria}
              className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
              <MagnifyingGlassIcon className="h-4 w-4" />
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
